import { useAppSelector, useTranslate } from '@src/core/hooks';
import { SaleFormInput, FormInputInputType } from '@src/core/buyerApiClient';
import React, { useMemo } from 'react';
import BrazilFlag from '../../icons/brasilflag.svg';
import { device } from '@src/common/breakpoints';
import { BodyExtraSmallMedium, BodySmallMedium } from '@src/fonts/typography/Typography';
import styled from 'styled-components';
import { ContainerSubtitleText, FinalFormValuesToSend } from './BuyerInfoCollect';
import { orderRequiredFormInputsSelector, RequiredFormInputsErrors, BRAZIL_PHONE_DDI } from '@src/features/order';

export const BuyerCollectInfoFormInputs = ({ index, formInputs, handleInputDataToSend }: FormContainerProps) => {
  const { i18nElement } = useTranslate();

  const orderRequiredFormInputs = useAppSelector(orderRequiredFormInputsSelector);
  const saleRequiredFormInputs = useAppSelector(orderRequiredFormInputsSelector);
  const finalRequiredFormInputs = useMemo(
    () => orderRequiredFormInputs ?? saleRequiredFormInputs,
    [orderRequiredFormInputs, saleRequiredFormInputs],
  );

  return (
    <FormContainer>
      <FormHeader>
        <ContainerSubtitleText>
          {i18nElement('checkout_tariff')} {index + 1} - {formInputs[0]?.saleTariff?.name}
        </ContainerSubtitleText>
      </FormHeader>

      <InputsGrid>
        {formInputs.map((input) => (
          <InputContainer key={input.hash}>
            <InputLabel>{input.formInput.name}</InputLabel>

            {input.inputType === FormInputInputType.Phone ? (
              <PhoneNumberInput>
                <PhoneDDI>
                  <BrazilFlag />
                  <p>{BRAZIL_PHONE_DDI}</p>
                </PhoneDDI>
                <PreparedInput {...{ input, errors: finalRequiredFormInputs?.errors ?? {}, handleInputDataToSend }} />
              </PhoneNumberInput>
            ) : (
              <PreparedInput {...{ input, errors: finalRequiredFormInputs?.errors ?? {}, handleInputDataToSend }} />
            )}
            {finalRequiredFormInputs?.errors[input.hash]
              ? finalRequiredFormInputs?.errors[input.hash]?.map((errorMessage, index) => (
                  <ErrorLabel key={`error-label-${index}`}>
                    {replaceHashByInputName(errorMessage, input.hash, input.formInput.name)}
                  </ErrorLabel>
                ))
              : null}
          </InputContainer>
        ))}
      </InputsGrid>
    </FormContainer>
  );
};

const PreparedInput = ({ input, errors, handleInputDataToSend }: PreparedInputProps) => {
  return (
    <Input
      placeholder={setPlaceholder(input.validationRules ?? null)}
      error={!!errors[input.hash]}
      type={input.inputType}
      onChange={(e) => {
        handleInputDataToSend({ value: e.target.value, hash: input.hash, inputType: input.inputType });
      }}
    />
  );
};

const replaceHashByInputName = (errorMessage: string, hash: string, inputName: string) => {
  return errorMessage.replace(hash, inputName);
};

const setPlaceholder = (rules: string[] | null) => {
  return rules && rules[0] ? placeholders[rules[0]] : '';
};

interface PreparedInputProps {
  handleInputDataToSend: (inputData: FinalFormValuesToSend) => void;
  input: SaleFormInput;
  errors: RequiredFormInputsErrors;
}

interface FormContainerProps {
  handleInputDataToSend: (inputData: FinalFormValuesToSend) => void;
  formInputs: SaleFormInput[];
  index: number;
}

interface PlaceholdersProps {
  [key: string]: string;
}

const placeholders: PlaceholdersProps = {
  phone_format: '(99) 99999-9999',
  email: 'seu@melhoremail.com',
  cpf_format: '000.000.000-00',
};

const FormContainer = styled.div`
  display: flex;
  flex-flow: column;
  gap: 16px;

  ${device.mediumTablet} {
    gap: 32px;
  }
`;

const FormHeader = styled.div`
  flex-flow: column;
  align-items: flex-start;

  ${device.smallTablet} {
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const InputsGrid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  gap: 16px;

  ${device.smallTablet} {
    display: grid;
    grid-template-columns: repeat(2, calc(50% - 8px));
    gap: 32px 16px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-flow: column;
  column-gap: 8px;
`;

const InputLabel = styled.div`
  ${BodySmallMedium};
  margin-bottom: 8px;
`;
const PhoneDDI = styled.div`
  width: 84px;
  border-radius: 4px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #949494;
  height: 46px;
  ${BodySmallMedium};
  padding: 6px;
`;
const PhoneNumberInput = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input<InputProps>`
  background-color: #ffffff;
  border: 1px solid ${(props) => (props.error ? '#C7372C' : '#949494')};
  border-radius: 4px;
  height: 46px;
  width: 100%;
  ${BodyExtraSmallMedium};
  padding: 12px;

  &::placeholder {
    opacity: 1;
    color: #949494;
  }
`;

const ErrorLabel = styled.p`
  ${BodyExtraSmallMedium};
  margin-top: 4px;
  color: #c7372c;
`;

interface InputProps {
  error: boolean;
}
